// account.service.ts
import { Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import { Account, GPSUnit } from './account.model';

interface FetchAccountsResponse {
  data: {
    accounts: Account[];
  };
}

// Interfaces for the API responses
interface FetchAccountResponse {
  data: {
    account: Account;
    roles: string[];
  };
}

interface UpdateAccountResponse {
  data: {
    account: Account;
  };
}

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  constructor(private apiService: ApiService) {}

  async fetchAll(): Promise<Account[]> {
    try {
      const response = await this.apiService.get<FetchAccountsResponse>('/account');
      return response.data.accounts;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Fetches the account details for the current user.
   *
   * @returns A promise that resolves with the account details object.
   * @throws If the request fails, throws the encountered error.
   */
  async fetch(): Promise<Account> {
    try {
      const response = await this.apiService.get<FetchAccountResponse>('/account/account-details');
      const { account, roles } = response.data || {};

      // Store roles in localStorage or a secure storage solution
      localStorage.setItem('ACCOUNT_ROLES', roles.join(','));

      return { ...account, roles: roles };
    } catch (error) {
      // Sentry.captureException(error);
      throw error;
    }
  }

  async create(accountDetails: Account): Promise<Account> {
    try {
      const newAccount = {
        firstName: accountDetails.firstName,
        lastName: accountDetails.lastName,
        email: accountDetails.email,
        phone: accountDetails.phone,
        country: accountDetails.country,
        callSign: accountDetails.callSign,
        unit: accountDetails.unit ?? '',
        gpsUnit: GPSUnit.UTM,
        language: 'EN_US',
      }

      const response = await this.apiService.post<UpdateAccountResponse>('/account', {
        account: newAccount,
        roles: accountDetails.roles,
        organizationId: accountDetails.organizationId
      });

      const { account } = response.data || {};

      return account;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Updates the account details for the current user.
   *
   * @param accountDetails The account details to update.
   * @returns A promise that resolves with the updated account object.
   * @throws If the request fails, throws the encountered error.
   */
  async update(accountDetails: Account): Promise<Account> {
    try {
      const response = await this.apiService.put<UpdateAccountResponse>(
        `/account/${accountDetails.id}`,
        accountDetails
      );
      const { account } = response.data || {};
      return account;
    } catch (error) {
      throw error;
    }
  }

  async delete(accountId: string): Promise<void> {
    try {
      await this.apiService.delete(`/account/${accountId}`);
    } catch (error) {
      throw error;
    }
  }
}
